import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
} from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

// Page Container - Main wrapper for all pages
export const PageContainer = ({ children, maxWidth = 'xl', sx = {} }) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: maxWidth === 'xl' ? '1400px' : 
                  maxWidth === 'lg' ? '1200px' : 
                  maxWidth === 'md' ? '960px' : '100%',
        mx: 'auto',
        width: '100%',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// Section Container - Wrapper for content sections
export const SectionContainer = ({ children, sx = {} }) => (
  <Box sx={{ mb: { xs: 3, sm: 4 }, ...sx }}>
    {children}
  </Box>
);

// Responsive Card Component
export const ResponsiveCard = ({ 
  title, 
  subtitle, 
  children, 
  action,
  elevation = 2,
  sx = {} 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Card
      elevation={elevation}
      sx={{
        borderRadius: { xs: 2, sm: 3 },
        height: 'fit-content',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
          borderColor: 'primary.light',
        },
        ...sx,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
        {(title || subtitle || action) && (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              mb: title || subtitle ? 3 : 0,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Box sx={{ flex: 1 }}>
              {title && (
                <Typography 
                  variant={isMobile ? 'h6' : 'h5'} 
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: subtitle ? 0.5 : 0,
                  }}
                >
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ opacity: 0.8 }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
            {action && (
              <Box sx={{ flexShrink: 0 }}>
                {action}
              </Box>
            )}
          </Box>
        )}
        {children}
      </CardContent>
    </Card>
  );
};

// Stats Card Component
export const StatCard = ({ 
  title, 
  value, 
  icon, 
  color = 'primary', 
  trend,
  progress,
  subtitle,
  onClick,
  actionButton,
  sx = {} 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp sx={{ color: 'success.main', fontSize: 16 }} />;
    if (trend === 'down') return <TrendingDown sx={{ color: 'error.main', fontSize: 16 }} />;
    return null;
  };

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: { xs: 2, sm: 3 },
        height: { xs: 'auto', sm: 173 }, // Fixed height for consistent sizing
        minHeight: { xs: 140, sm: 170 }, // Minimum height for mobile
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid',
        borderColor: 'divider',
        background: 'background.paper',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        } : {},
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
      onClick={onClick}
    >
      <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          sx={{
            backgroundColor: `${color}.main`,
            color: `${color}.contrastText`,
            width: { xs: 48, sm: 56 },
            height: { xs: 48, sm: 56 },
            flexShrink: 0,
          }}
        >
          {icon}
        </Avatar>
        
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="h4"
              sx={{ 
                fontWeight: 700,
                color: `${color}.main`,
                lineHeight: 1,
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
              }}
            >
              {value}
            </Typography>
            {getTrendIcon()}
          </Box>
          
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              textAlign: 'center',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            }}
          >
            {title}
          </Typography>
          
          {subtitle && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                display: 'block',
                lineHeight: 1.3,
                textAlign: 'center',
                maxWidth: '160px',
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 400,
              }}
            >
              {subtitle}
            </Typography>
          )}
          
          {actionButton && onClick && (
            <Button
              variant="outlined"
              size="small"
              sx={{ 
                mt: 0.5,
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 500,
                px: 2,
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}
            >
              {actionButton}
            </Button>
          )}
        </Box>

        {/* Vertical Progress Bar - Right side */}
        {progress !== undefined && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, ml: 1 }}>
            <Box 
              sx={{ 
                width: 8,
                height: 60,
                backgroundColor: `${color}.light`,
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute',
                  bottom: 0,
                  width: '100%',
                  height: `${progress}%`,
                  backgroundColor: `${color}.main`,
                  borderRadius: 4,
                  transition: 'height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
            </Box>
            
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 600,
                color: `${color}.main`,
                textAlign: 'center',
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}
            >
              {progress}%
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Stats Grid - Responsive grid for stat cards
export const StatsGrid = ({ children, sx = {} }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(1, 1fr)',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)',
        lg: 'repeat(auto-fit, minmax(280px, 1fr))',
      },
      gap: { xs: 2, sm: 3 },
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Content Grid - Main content layout grid
export const ContentGrid = ({ children, sx = {} }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        lg: '2fr 1fr',
      },
      gap: { xs: 3, sm: 4 },
      alignItems: 'start',
      ...sx,
    }}
  >
    {children}
  </Box>
);

// List Grid - Grid for list items
export const ListGrid = ({ children, columns, sx = {} }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(1, 1fr)',
        sm: `repeat(${columns || 2}, 1fr)`,
        md: `repeat(${columns || 3}, 1fr)`,
      },
      gap: { xs: 2, sm: 3 },
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Page Header Component
export const PageHeader = ({ 
  title, 
  subtitle, 
  action, 
  breadcrumbs,
  sx = {} 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <SectionContainer sx={sx}>
      {breadcrumbs && (
        <Box sx={{ mb: 2 }}>
          {breadcrumbs}
        </Box>
      )}
      
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant={isMobile ? 'h4' : 'h3'} 
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: subtitle ? 1 : 0,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        
        {action && (
          <Box sx={{ flexShrink: 0 }}>
            {action}
          </Box>
        )}
      </Box>
    </SectionContainer>
  );
};

// Empty State Component
export const EmptyState = ({ 
  icon, 
  title, 
  description, 
  action,
  sx = {} 
}) => (
  <Box 
    sx={{ 
      textAlign: 'center', 
      py: { xs: 6, sm: 8 },
      color: 'text.secondary',
      ...sx,
    }}
  >
    {icon && (
      <Box sx={{ mb: 3 }}>
        {React.cloneElement(icon, { 
          sx: { fontSize: { xs: 64, sm: 80 }, opacity: 0.3, ...icon.props.sx } 
        })}
      </Box>
    )}
    
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    
    {description && (
      <Typography variant="body2" sx={{ mb: action ? 3 : 0, maxWidth: 400, mx: 'auto' }}>
        {description}
      </Typography>
    )}
    
    {action && action}
  </Box>
);

export default {
  PageContainer,
  SectionContainer,
  ResponsiveCard,
  StatCard,
  StatsGrid,
  ContentGrid,
  ListGrid,
  PageHeader,
  EmptyState,
};